import { useState } from "react";
import Invoice from "./invoice";
import moment from "moment";
import { formatPrice } from "utils/convertNumbThousand";

const EXPENSIVE_CYCLE_THRESHOLD = 1.25; // 1NOW-1095: highlight cycles >25% over Week 1

function getPeriods(duration: number, nature: string, invoice?: any) {
  const results = [];
  let current = invoice?.pick_up_time ? moment(invoice.pick_up_time) : moment();
  let firstEnd = invoice?.drop_off_time ? moment(invoice.drop_off_time) : null;

  for (let i = 0; i < duration; i++) {
    let end;
    if (i === 0 && firstEnd) {
      end = firstEnd.clone();
    } else {
      end = nature === "weekly"
        ? current.clone().add(7, "days")
        : current.clone().add(30, "days");
    }

    results.push({
      label: nature === "weekly" ? `Week ${i + 1}` : `Month ${i + 1}`,
      range: `${current.format("MMM D")} - ${end.format("MMM D")}`,
    });
    current = end.clone();
  }

  return results;
}

// 1NOW-1095: Per-cycle invoice projection. When provided, each Week/Month
// shows its OWN total (not Week 1's repeated for all). Previously the
// component displayed `invoice?.total_price_without_security` for every
// period, which let a customer agree to e.g. $264 and later be charged
// $1137 on cycles 2/3/4 when the custom-price calendar inflated those
// weeks. `weeklyQuote` comes from `/api/bookings/long-term-weekly-quote/`.
export function hasExpensiveCycles(weeklyQuote?: any[]) {
  if (!Array.isArray(weeklyQuote) || weeklyQuote.length < 2) return false;
  const baseTotal = Number(
    weeklyQuote[0]?.invoice?.total_price_without_security ?? 0
  );
  if (!baseTotal) return false;
  return weeklyQuote.slice(1).some((cycle) => {
    const cycleTotal = Number(cycle?.invoice?.total_price_without_security ?? 0);
    return cycleTotal > baseTotal * EXPENSIVE_CYCLE_THRESHOLD;
  });
}

const LongTermRentalInvoice = ({
  long_term_rental_nature,
  long_term_rental_duration,
  invoice,
  carName,
  weeklyQuote,
}: any) => {
  const [openIndex, setOpenIndex] = useState<number>(-1);
  const periods = getPeriods(
    long_term_rental_duration,
    long_term_rental_nature,
    invoice,
  );

  const hasQuote =
    Array.isArray(weeklyQuote) && weeklyQuote.length === periods.length;
  const baseTotal = hasQuote
    ? Number(weeklyQuote[0]?.invoice?.total_price_without_security ?? 0)
    : 0;

  return (
    <div className="rounded-xl w-full">
      <h2 className="text-2xl font-bold text-text-color mb-3">
        Booking Invoice
      </h2>

      {periods.map((period, idx) => {
        const cycleInvoice = hasQuote ? weeklyQuote[idx]?.invoice : invoice;
        const cycleTotal = Number(
          cycleInvoice?.total_price_without_security ?? 0
        );
        const isExpensive =
          hasQuote &&
          idx > 0 &&
          baseTotal > 0 &&
          cycleTotal > baseTotal * EXPENSIVE_CYCLE_THRESHOLD;

        return (
          <div
            key={idx}
            className={`my-2 border rounded-lg ${
              isExpensive
                ? "border-orange-400 bg-orange-50"
                : "border-secondary-100"
            }`}
          >
            <button
              className="flex justify-between items-center w-full px-4 py-3 focus:outline-none"
              onClick={() => setOpenIndex(idx === openIndex ? -1 : idx)}
              aria-expanded={openIndex === idx}
            >
              <span className="text-left">
                <span className="text-lg font-[600] text-text-color">
                  {period.label}
                </span>
                <br />
                <span className="text-base font-[400] text-text-color">
                  {period.range}
                </span>
              </span>

              <span className="text-left">
                <span className="text-lg font-[600] text-text-color">Amount</span>
                <br />
                <span className="text-base font-[400] text-text-color">
                  ${formatPrice(cycleTotal)}
                </span>
              </span>
              <span className="ml-3 text-base text-primary-brand">
                {openIndex === idx ? (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 15.665C5 15.665 9.4189 9.66504 11 9.66504C12.5812 9.66504 17 15.665 17 15.665"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19 8.33501C19 8.33501 14.5811 14.335 13 14.335C11.4188 14.335 7 8.33496 7 8.33496"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                )}
              </span>
            </button>
            {openIndex === idx && (
              <div className="px-4 pb-4  border-t border-secondary-100">
                <Invoice invoice={cycleInvoice} carName={carName} />
              </div>
            )}
          </div>
        );
      })}

      {hasQuote && periods.length > 1 && (
        <div className="mt-3 flex justify-between border-t border-secondary-100 pt-3 text-base font-[600] text-text-color">
          <span>Projected total</span>
          <span>
            $
            {formatPrice(
              weeklyQuote.reduce(
                (sum: number, cycle: any) =>
                  sum +
                  Number(cycle?.invoice?.total_price_without_security ?? 0),
                0
              )
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default LongTermRentalInvoice;
